import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useDebounceValue } from 'usehooks-ts';
import { postAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';

import { FundBasicDetailsDtoIn } from '@/models/in/FundBasicDetailsDtoIn';
import { FundBalancesDtoIn, calculateAvailableBalance } from '@/models/in/FundManagementDetailsDtoIn';
import { InactiveFundsScanDtoIn } from '@/models/in/InactiveFundCandidateDtoIn';
import { PendingReactivationSummaryDtoIn } from '@/models/in/ReactivationRequestDtoIn';
import { FundStatus } from '@/models/common/FundStatus';
import DataTable from '@/components/ui/DataTable/DataTable';
import { Col } from '@/components/ui/DataTable/ColumnsHelpers';
import Button from '@/components/ui/Button';
import StatusFilter from '@/components/filters/StatusFilter/StatusFilter';
import { useGetAuthApi, usePostAuthApi, usePutAuthApi } from '@/hooks/UseApiCalls';
import useTableDensity from '@/hooks/useTableDensity';
import filterStyles from '@/components/filters/FiltersArea.module.scss';
import FundBlade, { FundBladeTab } from './FundBlade/FundBlade';
import { describeOverdraft, formatFeatureDate, formatOverdraftAmount, overdraftSortValue } from './fundOverdraft';
import { matchesFundsSearch } from './fundsManagementSearch';

const LOCAL_STORAGE_KEY = 'fundsManagementTableContext';
const DENSITY_STORAGE_KEY = 'table.density.fundsManagement';
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The list endpoint is per-status, so a row carries the status of the query it came from.
 * Balances arrive separately and are merged in, so they are undefined until they land.
 */
type FundRow = Omit<FundBasicDetailsDtoIn, 'balance' | 'balanceForWithdrawal'> & {
  status: FundStatus;
  balance?: number;
  balanceForWithdrawal?: number;
};

type StatusOption = FundStatus | 'All';

const STATUS_OPTIONS: StatusOption[] = ['All', FundStatus.Active, FundStatus.Frozen, FundStatus.Closed];

const STATUS_COLORS: Record<FundStatus, string> = {
  [FundStatus.Active]: '#16a34a',
  [FundStatus.Frozen]: '#d97706',
  [FundStatus.Closed]: '#dc2626',
};

/** HAT-1301: the stored result of the last inactivity check. Cheap to read; expensive to compute. */
const INACTIVE_CANDIDATES_URL = 'funds/management/inactive-candidates';

/** HAT-1301: frozen funds whose coordinator has sent identity documents, waiting for the office. */
const PENDING_REQUESTS_URL = 'funds/management/reactivation-requests';

/** How often the screen asks whether a running recompute has finished. */
const SCAN_POLL_INTERVAL = 4000;

function getInitialStatus(): StatusOption {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.status && STATUS_OPTIONS.includes(parsed.status)) return parsed.status;
    }
  } catch {
    // ignore parse errors
  }
  return FundStatus.Active;
}

function saveStatus(status: StatusOption) {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({ status }));
}

/**
 * Balances are computed per fund in the accounting system, so asking for all ~2,300 at once
 * takes minutes. Splitting the ids into batches lets the columns fill in as each batch lands.
 *
 * The first batch is small and covers the rows the user actually sees (the table opens sorted
 * by fund number, and the ids are batched in that same order), so the visible screen fills on
 * the first round-trip while the rest stream in behind it.
 */
const FIRST_BALANCES_BATCH_SIZE = 50;
const BALANCES_BATCH_SIZE = 250;

/**
 * How many balance requests may be in flight at once. Firing all the batches together saturates
 * both the browser's connection pool and the database, so opening a fund's blade (which needs a
 * free connection and a responsive server) used to queue behind them for the whole load.
 */
const MAX_CONCURRENT_BALANCE_REQUESTS = 2;

const BALANCES_STALE_TIME = 5 * 60 * 1000;

function toBatches(ids: number[]): number[][] {
  if (ids.length === 0) return [];
  const batches: number[][] = [ids.slice(0, FIRST_BALANCES_BATCH_SIZE)];
  for (let i = FIRST_BALANCES_BATCH_SIZE; i < ids.length; i += BALANCES_BATCH_SIZE) {
    batches.push(ids.slice(i, i + BALANCES_BATCH_SIZE));
  }
  return batches;
}

const FundsManagementPage = () => {
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuthContext();
  const queryClient = useQueryClient();

  const [selectedStatus, setSelectedStatus] = useState<StatusOption>(getInitialStatus);
  // HAT-1285: the office asked for a list of the funds that have an overdraft frame ("מסגרת").
  const [onlyWithOverdraft, setOnlyWithOverdraft] = useState(false);
  // HAT-1301: the active funds the inactivity check would freeze today.
  const [onlyInactiveCandidates, setOnlyInactiveCandidates] = useState(false);
  // HAT-1301: frozen funds whose coordinator has sent identity documents, waiting for the office.
  const [onlyWithPendingRequest, setOnlyWithPendingRequest] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch] = useDebounceValue(searchQuery, SEARCH_DEBOUNCE_MS);
  const [density, setDensity] = useTableDensity(DENSITY_STORAGE_KEY, 'normal');
  const [selectedFund, setSelectedFund] = useState<FundRow | null>(null);
  const [bladeTab, setBladeTab] = useState<FundBladeTab>('details');

  // Both lists are fetched so the status column stays meaningful and the filter can show counts.
  const { data: activeFunds, isLoading: isActiveLoading } = useGetAuthApi<FundBasicDetailsDtoIn[]>(
    `funds/management?status=${FundStatus.Active}`
  );
  const { data: closedFunds, isLoading: isClosedLoading } = useGetAuthApi<FundBasicDetailsDtoIn[]>(
    `funds/management?status=${FundStatus.Closed}`
  );
  const { data: frozenFunds, isLoading: isFrozenLoading } = useGetAuthApi<FundBasicDetailsDtoIn[]>(
    `funds/management?status=${FundStatus.Frozen}`
  );

  // The stored result of the last run - one row on the server. It is NOT computed here: pricing
  // every fund that has stood still takes minutes, and this very screen is already computing
  // balances for the whole list in the background. Asking for both at once left the request
  // pending for nine minutes on staging, with the table blanked out while it waited.
  // While a recompute is going the server says so, and this polls until it finishes - the run
  // itself happens away from any request, so there is nothing to wait on here.
  const { data: scan } = useGetAuthApi<InactiveFundsScanDtoIn>(INACTIVE_CANDIDATES_URL, undefined, {
    refetchInterval: (query) => (query.state.data?.isRunning ? SCAN_POLL_INTERVAL : false),
  });
  // A short list the server reads in one query: the office needs to find these without opening
  // every frozen fund, because a coordinator is waiting on each of them.
  const { data: pendingRequests } = useGetAuthApi<PendingReactivationSummaryDtoIn[]>(PENDING_REQUESTS_URL);
  const pendingRequestIds = useMemo(
    () => new Set((pendingRequests ?? []).map((request) => request.fundId)),
    [pendingRequests]
  );
  const inactiveCandidates = scan?.candidates;
  const candidatesById = useMemo(
    () => new Map((inactiveCandidates ?? []).map((candidate) => [candidate.fundId, candidate])),
    [inactiveCandidates]
  );

  // Recomputing is an explicit action, never a side effect of opening the screen. The request only
  // starts the run; the poll above reports when it is done.
  // Invalidated rather than written into the cache: useGetAuthApi puts a function in its query key,
  // so writing that key by hand would rely on how it happens to hash. Re-reading it costs one row.
  const invalidateScan = useCallback(
    () => queryClient.invalidateQueries({ queryKey: [INACTIVE_CANDIDATES_URL] }),
    [queryClient]
  );
  const rescan = usePostAuthApi<InactiveFundsScanDtoIn, object>(`${INACTIVE_CANDIDATES_URL}/run`, {
    onSuccess: invalidateScan,
  });
  const setDailyCheck = usePutAuthApi<InactiveFundsScanDtoIn, object, { enabled: boolean }>(
    `${INACTIVE_CANDIDATES_URL}/daily-check`,
    { onSuccess: invalidateScan }
  );
  // Safe to offer next to the switch above: the daily run freezes, but never destroys documents.
  const setAutoFreeze = usePutAuthApi<InactiveFundsScanDtoIn, object, { enabled: boolean }>(
    `${INACTIVE_CANDIDATES_URL}/auto-freeze`,
    { onSuccess: invalidateScan }
  );
  const isScanning = (scan?.isRunning ?? false) || rescan.isPending;

  const allRows: FundRow[] = useMemo(() => [
    ...(activeFunds ?? []).map((fund) => ({ ...fund, status: FundStatus.Active })),
    ...(frozenFunds ?? []).map((fund) => ({ ...fund, status: FundStatus.Frozen })),
    ...(closedFunds ?? []).map((fund) => ({ ...fund, status: FundStatus.Closed })),
  ], [activeFunds, frozenFunds, closedFunds]);

  // The fund list itself carries no balances; they come from the bulk balances endpoint,
  // requested in batches so the columns fill in progressively instead of after minutes.
  //
  // Only the funds of the selected tab are requested - balances for the other tab would be
  // computed and transferred for rows nobody is looking at, and are fetched when the user
  // actually switches to it. Each status is batched separately so a batch's identity (its
  // query key) is the same no matter which tab it is requested from: switching Active -> All
  // reuses the already-fetched active batches and only fetches the closed ones. The ids are
  // sorted by fund number so batches land in the table's default display order, visible rows
  // first.
  const balanceBatches = useMemo(() => {
    const idsOf = (funds: FundBasicDetailsDtoIn[] | undefined) =>
      (funds ?? []).map((fund) => fund.id).sort((a, b) => a - b);

    const isShown = (status: FundStatus) => selectedStatus === 'All' || selectedStatus === status;

    return [
      ...(isShown(FundStatus.Active) ? toBatches(idsOf(activeFunds)) : []),
      ...(isShown(FundStatus.Frozen) ? toBatches(idsOf(frozenFunds)) : []),
      ...(isShown(FundStatus.Closed) ? toBatches(idsOf(closedFunds)) : []),
    ];
  }, [activeFunds, frozenFunds, closedFunds, selectedStatus]);
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${currentUser?.token}` }),
    [currentUser?.token]
  );

  // Batches run MAX_CONCURRENT_BALANCE_REQUESTS at a time instead of all at once: a batch is
  // enabled only once enough earlier batches have settled. Settling re-renders the page (the
  // queries are subscribed below), which re-evaluates the gate and releases the next batch.
  // A stale success does not count as settled - on a revisit after the data expired, stale
  // batches refetch on mount, and counting them as settled would enable all of them at once.
  const settledBatchesCount = balanceBatches.filter((fundIds) => {
    const state = queryClient.getQueryState(['funds/financial-data/balances', fundIds]);
    if (!state) return false;
    return state.status === 'error'
      || (state.status === 'success' && Date.now() - state.dataUpdatedAt < BALANCES_STALE_TIME);
  }).length;

  const balanceQueries = useQueries({
    queries: balanceBatches.map((fundIds, index) => ({
      queryKey: ['funds/financial-data/balances', fundIds],
      queryFn: () => postAuth<FundBalancesDtoIn[], { fundIds: number[] }>(
        'funds/financial-data/balances',
        authHeaders,
        { fundIds }
      ),
      staleTime: BALANCES_STALE_TIME,
      enabled: !!currentUser?.token && index < settledBatchesCount + MAX_CONCURRENT_BALANCE_REQUESTS,
    })),
  });

  const areListsLoading = isActiveLoading || isClosedLoading || isFrozenLoading;
  const isFiltersBusy = areListsLoading;
  const areBalancesLoading = areListsLoading || balanceQueries.some((query) => query.isPending);
  const showBalancesLoadingCue = areBalancesLoading && !areListsLoading;

  const balancesByFundId = useMemo(() => {
    const map = new Map<number, FundBalancesDtoIn>();
    balanceQueries.forEach((query) => {
      query.data?.forEach((balance) => map.set(balance.fundId, balance));
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balanceQueries.map((query) => query.dataUpdatedAt).join(',')]);

  // Balances are merged into the rows rather than read in an accessor, because the table
  // caches accessor results against the data identity and would keep showing the old values.
  const statusRows = useMemo(() => (
    selectedStatus === 'All' ? allRows : allRows.filter((fund) => fund.status === selectedStatus)
  ), [allRows, selectedStatus]);

  const withOverdraftCount = useMemo(
    () => statusRows.filter((fund) => fund.overdraft).length,
    [statusRows]
  );

  const rows = useMemo(() => {
    let filtered = onlyWithOverdraft ? statusRows.filter((fund) => fund.overdraft) : statusRows;
    if (onlyInactiveCandidates) {
      filtered = filtered.filter((fund) => candidatesById.has(fund.id));
    }
    if (onlyWithPendingRequest) {
      filtered = filtered.filter((fund) => pendingRequestIds.has(fund.id));
    }

    return filtered.map((fund) => {
      const balance = balancesByFundId.get(fund.id);
      return {
        ...fund,
        balance: balance ? balance.balancesByKind.total : undefined,
        balanceForWithdrawal: balance ? calculateAvailableBalance(balance) : undefined,
      };
    });
  }, [statusRows, onlyWithOverdraft, onlyInactiveCandidates, candidatesById, onlyWithPendingRequest, pendingRequestIds, balancesByFundId]);

  const tableRows = useMemo(
    () => rows.filter((fund) => matchesFundsSearch(fund, debouncedSearch)),
    [rows, debouncedSearch]
  );

  const counts = useMemo(() => ({
    All: allRows.length,
    [FundStatus.Active]: activeFunds?.length ?? 0,
    [FundStatus.Frozen]: frozenFunds?.length ?? 0,
    [FundStatus.Closed]: closedFunds?.length ?? 0,
  }), [allRows.length, activeFunds, frozenFunds, closedFunds]);

  const handleStatusChange = useCallback((status: StatusOption) => {
    if (isFiltersBusy) return;
    setSelectedStatus(status);
    saveStatus(status);
  }, [isFiltersBusy]);

  const toggleWithOverdraft = useCallback(() => {
    if (isFiltersBusy) return;
    setOnlyWithOverdraft((value) => !value);
  }, [isFiltersBusy]);

  /**
   * Candidates are Active funds by definition, so leaving a status filter of "frozen" or "closed"
   * on would have shown an empty table with nothing to explain it - which is exactly how it read
   * the first time someone tried it. Turning the filter on widens the status to All.
   */
  const toggleInactiveCandidates = useCallback(() => {
    if (isFiltersBusy) return;
    setOnlyInactiveCandidates((value) => {
      if (!value && selectedStatus !== 'All' && selectedStatus !== FundStatus.Active) {
        handleStatusChange('All');
      }
      return !value;
    });
  }, [selectedStatus, handleStatusChange, isFiltersBusy]);

  /**
   * A request can only exist on a frozen fund, so a status filter of "active" or "closed" would
   * show an empty table with nothing to explain it - the same trap as the candidates filter.
   * Turning this on widens the status to All.
   */
  const togglePendingRequests = useCallback(() => {
    if (isFiltersBusy) return;
    setOnlyWithPendingRequest((value) => {
      if (!value && selectedStatus !== 'All' && selectedStatus !== FundStatus.Frozen) {
        handleStatusChange('All');
      }
      return !value;
    });
  }, [selectedStatus, handleStatusChange, isFiltersBusy]);

  const openBlade = useCallback((fund: FundRow, tab: FundBladeTab) => {
    setSelectedFund(fund);
    setBladeTab(tab);
  }, []);

  const amountCell = useCallback((value: number | undefined) => {
    if (value === undefined) return <span className="text-(--font-body-3)">{areBalancesLoading ? '…' : '—'}</span>;
    return (
      <span dir="ltr" className={value < 0 ? 'text-red-600' : ''}>
        {value.toLocaleString(i18n.language, { style: 'currency', currency: 'ILS' })}
      </span>
    );
  }, [i18n.language, areBalancesLoading]);

  const columns: ColumnDef<FundRow>[] = [
    {
      id: 'id',
      header: Col(t('fundsManagement.fundNumber')),
      accessorKey: 'id',
      meta: { className: 'max-w-[8%] min-w-[8%] w-[8%]' },
    },
    {
      id: 'name',
      header: Col(t('fundsManagement.fundName')),
      accessorKey: 'name',
      meta: { className: 'max-w-[15%] min-w-[15%] w-[15%]' },
    },
    {
      id: 'status',
      header: Col(t('fundsManagement.statusColumn')),
      accessorKey: 'status',
      cell: ({ getValue }) => {
        const status = getValue() as FundStatus;
        return (
          <div className="flex items-center gap-2">
            <div className="w-[10px] h-[10px] rounded-full" style={{ backgroundColor: STATUS_COLORS[status] }} />
            <span>{t(`fundsManagement.status.${status}`)}</span>
          </div>
        );
      },
      meta: { className: 'max-w-[9%] min-w-[9%] w-[9%]' },
    },
    {
      id: 'balance',
      header: Col(t('fundsManagement.balance')),
      accessorKey: 'balance',
      cell: ({ getValue }) => amountCell(getValue() as number | undefined),
      meta: { className: 'max-w-[12%] min-w-[12%] w-[12%]' },
    },
    {
      id: 'balanceForWithdrawal',
      header: Col(t('fundsManagement.balanceForWithdrawal')),
      accessorKey: 'balanceForWithdrawal',
      cell: ({ getValue }) => amountCell(getValue() as number | undefined),
      meta: { className: 'max-w-[12%] min-w-[12%] w-[12%]' },
    },
    {
      id: 'classification',
      header: Col(t('fundsManagement.classification')),
      accessorKey: 'classification',
      cell: ({ getValue }) => <div>{t(`fundsManagement.classificationNames.${getValue()}`)}</div>,
      meta: { className: 'max-w-[9%] min-w-[9%] w-[9%]' },
    },
    {
      id: 'overdraft',
      header: Col(t('fundsManagement.overdraft')),
      // A string, so the Excel export and the sort key below need no special casing.
      accessorFn: (row) => describeOverdraft(row.overdraft, t, i18n.language),
      sortingFn: (a, b) => overdraftSortValue(a.original.overdraft) - overdraftSortValue(b.original.overdraft),
      cell: ({ row }) => {
        const { overdraft } = row.original;
        if (!overdraft) return <span className="text-(--font-body-3)">—</span>;
        const full = describeOverdraft(overdraft, t, i18n.language);
        return (
          <span
            title={overdraft.reason ? `${full} · ${overdraft.reason}` : full}
            className={overdraft.isCurrentlyActive ? undefined : 'text-(--font-body-3)'}
          >
            <span dir="ltr">{formatOverdraftAmount(overdraft.amount, i18n.language)}</span>
            <span className="text-(--font-body-3)" style={{ marginInlineStart: '6px' }}>
              {overdraft.isCurrentlyActive
                ? t('fundsManagement.overdraftUntil', { date: formatFeatureDate(overdraft.endDate, t) })
                : t('fundsManagement.overdraftInactive')}
            </span>
          </span>
        );
      },
      meta: { className: 'max-w-[16%] min-w-[16%] w-[16%]' },
    },
    {
      id: 'coordinatorsAction',
      header: () => '',
      enableSorting: false,
      cell: ({ row }) => (
        <Button
          disabled={isFiltersBusy}
          onClick={(e) => {
            e.stopPropagation();
            if (isFiltersBusy) return;
            openBlade(row.original, 'coordinators');
          }}
          className="border border-(--border-main) px-4 py-2 flex items-center min-h-[28px]"
        >
          {t('fundsManagement.coordinators')}
        </Button>
      ),
      meta: { className: 'max-w-[9%] min-w-[9%] w-[9%]', hiddenOnMobile: true, lockLayout: true },
    },
    {
      id: 'supportedsAction',
      header: () => '',
      enableSorting: false,
      cell: ({ row }) => (
        <Button
          disabled={isFiltersBusy}
          onClick={(e) => {
            e.stopPropagation();
            if (isFiltersBusy) return;
            openBlade(row.original, 'supporteds');
          }}
          className="border border-(--border-main) px-4 py-2 flex items-center min-h-[28px]"
        >
          {t('fundsManagement.supporteds')}
        </Button>
      ),
      meta: { className: 'max-w-[9%] min-w-[9%] w-[9%]', hiddenOnMobile: true, lockLayout: true },
    },
  ];

  return (
    <div>
      {selectedFund && (
        <FundBlade
          key={`${selectedFund.id}-${bladeTab}`}
          fundId={selectedFund.id}
          fundName={selectedFund.name}
          initialTab={bladeTab}
          inactivity={candidatesById.get(selectedFund.id) ?? null}
          handleXclick={() => setSelectedFund(null)}
        />
      )}

      <DataTable
        title={t('fundsManagement.title')}
        data={tableRows}
        isLoading={areListsLoading}
        columns={columns}
        sortBy={{ id: 'id', desc: false }}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        density={density}
        onDensityChange={setDensity}
        columnLayoutKey="fundsManagement"
        searchPlaceholder="fundsManagement.searchShort"
        searchTooltip="fundsManagement.searchPlaceholder"
        tableHeight="calc(100vh - 340px)"
        customGlobalSearch={() => true}
        handleRowClick={(fund) => openBlade(fund, 'details')}
        activeRecord={selectedFund ?? undefined}
        exportColumns={columns.filter((col) => !col.id?.endsWith('Action'))}
        exportData={tableRows}
        exportFilename={t('fundsManagement.exportFilename')}
        filters={
          <div className={filterStyles.filtersArea}>
            <div className={filterStyles.filtersContainer}>
              {STATUS_OPTIONS.map((status) => (
                <StatusFilter
                  key={status}
                  title={status === 'All' ? 'fundsManagement.status.All' : `fundsManagement.status.${status}`}
                  isSelected={selectedStatus === status}
                  onClick={() => handleStatusChange(status)}
                  iconColor={status === 'All' ? undefined : STATUS_COLORS[status]}
                  count={counts[status]}
                  disabled={isFiltersBusy}
                />
              ))}
              <StatusFilter
                title="fundsManagement.withOverdraft"
                isSelected={onlyWithOverdraft}
                onClick={toggleWithOverdraft}
                count={withOverdraftCount}
                disabled={isFiltersBusy}
              />
              <StatusFilter
                title="fundsManagement.inactiveCandidates"
                isSelected={onlyInactiveCandidates}
                onClick={toggleInactiveCandidates}
                count={inactiveCandidates?.length}
                disabled={isFiltersBusy}
              />
              {/* Only when there is something waiting (or the filter is on, so it can be turned
                  off): an always-visible "0" chip is clutter on a screen that already has six. */}
              {(pendingRequestIds.size > 0 || onlyWithPendingRequest) && (
                <StatusFilter
                  title="fundsManagement.pendingRequest.filter"
                  isSelected={onlyWithPendingRequest}
                  onClick={togglePendingRequests}
                  count={pendingRequestIds.size}
                  disabled={isFiltersBusy}
                />
              )}
            </div>
            {showBalancesLoadingCue && (
              <p className="body-sm-regular text-(--font-body-2) mt-2" aria-live="polite">
                {t('fundsManagement.balancesLoading')}
              </p>
            )}
            {/* HAT-1301: the list is a snapshot, so it says when it was taken and whether anything
                will refresh it. An office that does not want to wait can take a fresh one. */}
            {onlyInactiveCandidates && (
              <div className="flex flex-wrap items-center gap-3 body-sm-regular text-(--font-body-2) mt-2">
                <span>
                  {scan?.runAt
                    ? t(scan.isDailyCheckEnabled ? 'fundsManagement.inactivity.asOfDaily' : 'fundsManagement.inactivity.asOfManual', {
                        date: new Date(scan.runAt).toLocaleString('he-IL'),
                        years: scan.inactivityYears,
                      })
                    : t(scan?.isDailyCheckEnabled ? 'fundsManagement.inactivity.neverRanDaily' : 'fundsManagement.inactivity.neverRan')}
                </span>
                <button
                  type="button"
                  className="body-sm-semibold text-(--font-link) underline bg-transparent border-0 cursor-pointer disabled:opacity-60"
                  disabled={isScanning}
                  onClick={() => rescan.mutate({})}
                >
                  {isScanning ? t('fundsManagement.inactivity.recomputing') : t('fundsManagement.inactivity.recompute')}
                </button>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={scan?.isDailyCheckEnabled ?? false}
                    disabled={setDailyCheck.isPending || !scan}
                    onChange={(e) => setDailyCheck.mutate({ params: {}, body: { enabled: e.target.checked } })}
                  />
                  <span>{t('fundsManagement.inactivity.dailyCheckToggle')}</span>
                </label>
                {/* Offered only once the daily check runs at all - freezing automatically is
                    meaningless without it, and the pair reads as one decision. */}
                {scan?.isDailyCheckEnabled && (
                  <label className="flex items-center gap-2 cursor-pointer" title={t('fundsManagement.inactivity.autoFreezeHint')}>
                    <input
                      type="checkbox"
                      checked={scan?.isAutoFreezeEnabled ?? false}
                      disabled={setAutoFreeze.isPending}
                      onChange={(e) => setAutoFreeze.mutate({ params: {}, body: { enabled: e.target.checked } })}
                    />
                    <span>{t('fundsManagement.inactivity.autoFreezeToggle')}</span>
                  </label>
                )}
                {scan?.lastError && (
                  <span className="text-red-600">{t('fundsManagement.inactivity.lastRunFailed')}</span>
                )}
              </div>
            )}
          </div>
        }
      />
    </div>
  );
};

export default FundsManagementPage;
